import React, { useState, useEffect, useRef, useContext } from 'react';
import { Form, Button, Spinner, Row, Col, Card, InputGroup, ListGroup } from 'react-bootstrap';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FaUser, FaBuilding, FaCalendarAlt, FaClock, FaRegTimesCircle } from 'react-icons/fa';
import salasService from '../../api/salasService';
import horariosService from '../../api/horariosService';
import profissionaisService from '../../api/profissionaisService';
import agendamentosService from '../../api/agendamentosService';
import { NotificationContext } from '../../contexts/notification-context';
import './index.css';

const Agendar = () => {
  const obterDataInicial = () => {
    const hoje = new Date();
    if (hoje.getDay() === 0) {
      hoje.setDate(hoje.getDate() + 1);
    }
    return hoje;
  };
  const { addNotification } = useContext(NotificationContext);
  const [salas, setSalas] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [salaSelecionada, setSalaSelecionada] = useState(null);
  const [dataSelecionada, setDataSelecionada] = useState(obterDataInicial());
  const [horaSelecionada, setHoraSelecionada] = useState(null);
  const [profissionalNome, setProfissionalNome] = useState(null);
  const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
  const [tipoSelecionado, setTipoSelecionado] = useState(true);
  const [particular, setParticular] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weekDates, setWeekDates] = useState([]);

  const inputRef = useRef(null);
  const sugestaoRef = useRef(null);

  const generateWeekDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = -2; dates.length < 15; i++) {
      const date = addDays(today, i);
      if (date.getDay() !== 0) { 
        dates.push(date);
      }
    }
    setWeekDates(dates);
};

  useEffect(() => {
    generateWeekDates();
  }, []);

  const formatDate = (date) => {
    return date ? format(date, 'dd/MM/yyyy (EEEE)', { locale: ptBR }) : '';
  };

  const clearInputProfissional = () => {
    setProfissionalNome('');
    setProfissionalSelecionado(null);
    setDataSelecionada(obterDataInicial());
    setParticular(false);
    setHorariosDisponiveis([]);
  };

  useEffect(() => {
    const fetchSalas = async () => {
      try {
        const response = await salasService.listar();
        setSalas(response);
      } catch (err) {
        console.error('Erro ao buscar salas:', err);
        addNotification('danger', 'Erro ao buscar salas disponíveis.');
      }
    };
    fetchSalas();
  }, []);

  useEffect(() => {
    const fetchProfissionais = async () => {
      try {
        const response = await profissionaisService.listar();
        setProfissionais(response);
      } catch (err) {
        console.error('Erro ao buscar profissionais:', err);
      }
    };
    fetchProfissionais();
  }, []);

  useEffect(() => {
    if (salaSelecionada && dataSelecionada) {
      const fetchHorarios = async () => {
        try {
          setHorariosDisponiveis([]);
          const response = await horariosService.disponiveis(salaSelecionada, dataSelecionada);
          setHorariosDisponiveis(response.length > 0 ? response : []);
          if (response.length === 0) {
            addNotification('warning', 'Não há horários disponíveis para esta sala.');
          }
        } catch (err) {
          console.error('Erro ao buscar horários:', err);
          addNotification('danger', 'Erro ao buscar horários disponíveis.');
        }
      };
      fetchHorarios();
    }
  }, [salaSelecionada, dataSelecionada]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "nome":
        setProfissionalNome(value);
        const normalizedValue = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        setSugestoes(
          profissionais.filter(profissional =>
            profissional.nome.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(normalizedValue)
          )
        );
        break;
      case "data":
        setDataSelecionada(new Date(value)); 
        setSalaSelecionada('');
        break;
      case "sala":
        setSalaSelecionada(value);
        break;
      case "tipo":
        setTipoSelecionado(value === "1");
        break;
      case "horario":
        setHoraSelecionada(value);
        break;
      default:
    }
  };

  const handleSugestaoClick = (id, nome, externo) => {
    setProfissionalSelecionado(id);
    setProfissionalNome(nome);
    setParticular(externo);
    setSugestoes([]);
  };

  const handleClickOutside = (event) => {
    if (
      inputRef.current && !inputRef.current.contains(event.target) &&
      sugestaoRef.current && !sugestaoRef.current.contains(event.target)
    ) {
      setSugestoes([]);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!salaSelecionada || !dataSelecionada || !horaSelecionada || !profissionalSelecionado) {
      addNotification('danger', 'Por favor, preencha todos os campos.');
      setLoading(false);
      return;
    }

    const novoAgendamento = {
      salaId: salaSelecionada,
      data: dataSelecionada,
      horarioId: horaSelecionada,
      profissionalId: profissionalSelecionado,
      modalidadeId: 1,
      avulso: tipoSelecionado,
      particular: particular
    };

    try {
      await agendamentosService.inserir(novoAgendamento);
      addNotification('success', 'Agendamento realizado com sucesso!');
      clearInputProfissional();
      setSalaSelecionada('');
      setHoraSelecionada('');
      setTipoSelecionado(true);
      setParticular(false);
    } catch (err) {
      console.error('Erro ao criar agendamento:', err);
      addNotification('danger', 'Erro ao criar agendamento. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agendar-container">
      <Card className="agendar-card shadow-sm">
        <Card.Body>
          <Card.Title className="text-center mb-4">Agendar Sala</Card.Title>
          <hr />

          <Form onSubmit={handleSubmit} className="agendar-form">
            <Row>
              <Col md={12} sm={12}>
                <Form.Group controlId="nome">
                  <Form.Label>Nome do Profissional</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaUser />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="nome"
                      value={profissionalNome}
                      onChange={handleChange}
                      required
                      placeholder="Digite o nome do profissional"
                      ref={inputRef}
                    />
                    {profissionalNome && (
                      <span onClick={clearInputProfissional} style={{
                        cursor: 'pointer',
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                      }}>
                        <FaRegTimesCircle />
                      </span>
                    )}
                  </InputGroup>
                  {sugestoes.length > 0 && (
                    <ListGroup ref={sugestaoRef}>
                      {sugestoes?.map((profissional, index) => (
                        <ListGroup.Item
                          key={index}
                          action
                          onClick={() => handleSugestaoClick(profissional.id, profissional.nome, profissional.externo)}
                        >
                          <b>{profissional.nome}</b>
                          <div>
                            <div>
                              {profissional.especialidade?.nome}
                            </div>
                            <div>
                              {` ${profissional.numeroRegistro}-${profissional.ufConselho}`}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className='mt-3'>
              <Col md={4} sm={12}>
                <Form.Group controlId="data">
                  <Form.Label>Data</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaCalendarAlt />
                    </InputGroup.Text>
                    <Form.Select
                      type="date"
                      name="data"
                      value={dataSelecionada.toISOString().split('T')[0]} 
                      onChange={handleChange}
                      required
                    >
                      {weekDates.map((date, index) => (
                        <option key={index} value={date.toISOString().split('T')[0]}>
                          {formatDate(date)}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4} sm={12}>
                <Form.Group controlId="sala">
                  <Form.Label>Sala</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaBuilding />
                    </InputGroup.Text>
                    <Form.Control
                      as="select"
                      className="form-select"
                      name="sala"
                      value={salaSelecionada}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Selecione uma sala</option>
                      {salas?.map(sala => (
                        <option key={sala.id} value={sala.id}>{sala.nome}</option>
                      ))}
                    </Form.Control>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4} sm={12}>
                <Form.Group controlId="horario">
                  <Form.Label>Horário</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaClock />
                    </InputGroup.Text>
                    <Form.Control
                      as="select"
                      className="form-select"
                      name="horario"
                      value={horaSelecionada}
                      onChange={handleChange}
                      required
                    >
                      <option value="" selected>Selecione um horário</option>
                      {horariosDisponiveis?.map((horarioDisp, index) => (
                        <option key={index} value={horarioDisp.id}>{horarioDisp.horario}</option>
                      ))}
                    </Form.Control>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row className='mt-3 mb-3'>
              <Col md={3} sm={12}>
                <Form.Group controlId="tipo">
                  <Form.Label>Tipo de Agendamento</Form.Label>
                  <Form.Control
                    as="select"
                    className="form-select"
                    name="tipo"
                    value={tipoSelecionado ? "1" : "0"}
                    onChange={handleChange}
                  >
                    <option value="1">Avulso</option>
                    <option value="0">Fixo</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3} sm={12}>
                <Form.Group controlId="particular">
                  <Form.Label>Particular</Form.Label>
                  <Form.Check
                    type="switch"
                    id="particular-switch"
                    checked={particular}
                    onChange={(e) => setParticular(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
                <Col md={2} className="text-start">
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Agendar'}
                  </Button>
                </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Agendar;